import { useState, useMemo } from "react";

interface CourseOption {
  key: string;
  name: string;
  subtitle: string;
  icon: string;
  priceAVista: string;
  priceParcela: string;
  nParcelas: number;
  totalParcelado: string;
  formUrl: string;
  kiwifyUrl: string;
}

const COURSES: CourseOption[] = [
  {
    key: "vendedora",
    name: "Vendedora",
    subtitle: "Método C.A.T",
    icon: "ri-seedling-line",
    priceAVista: "R$ 498,50",
    priceParcela: "R$ 53,00",
    nParcelas: 10,
    totalParcelado: "R$ 593,00",
    formUrl: "https://readdy.ai/api/form/d70rb0hqa83q0hmd1fv0",
    kiwifyUrl: "https://pay.kiwify.com.br/MThLhMv",
  },
  {
    key: "gerente",
    name: "Gerente",
    subtitle: "Método C.A.T",
    icon: "ri-user-star-line",
    priceAVista: "R$ 984,50",
    priceParcela: "R$ 116,00",
    nParcelas: 10,
    totalParcelado: "R$ 1.160,00",
    formUrl: "https://readdy.ai/api/form/d70rb0hqa83q0hmd1fvg",
    kiwifyUrl: "https://pay.kiwify.com.br/tJPadIg",
  },
  {
    key: "lojista",
    name: "Lojista",
    subtitle: "Método C.A.T",
    icon: "ri-store-2-line",
    priceAVista: "R$ 1.894,40",
    priceParcela: "R$ 224,00",
    nParcelas: 10,
    totalParcelado: "R$ 2.240,00",
    formUrl: "https://readdy.ai/api/form/d70rb0hqa83q0hmd1g00",
    kiwifyUrl: "https://pay.kiwify.com.br/Utu5OHX",
  },
];

interface EnrollmentFormProps {
  defaultCourseKey: string;
}

type Status = "idle" | "loading" | "success" | "error";

export default function EnrollmentForm({ defaultCourseKey }: EnrollmentFormProps) {
  const [status, setStatus] = useState<Status>("idle");
  const [selectedPlan, setSelectedPlan] = useState<"full" | "installment">("full");
  const [selectedCourseKey, setSelectedCourseKey] = useState(defaultCourseKey);

  const course = useMemo(
    () => COURSES.find((c) => c.key === selectedCourseKey) ?? COURSES[0],
    [selectedCourseKey]
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => params.append(key, value.toString()));
    params.append(
      "plano",
      selectedPlan === "full"
        ? `À vista — ${course.priceAVista}`
        : `Parcelado ${course.nParcelas}x ${course.priceParcela} sem juros`
    );
    params.append("curso", `${course.subtitle} — ${course.name}`);
    setStatus("loading");
    try {
      await fetch(course.formUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      window.open(course.kiwifyUrl, "_blank");
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="inscricao" className="py-20 px-6 md:px-16 bg-[#f9f8f6]">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          {/* Left: pricing */}
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#b8962e] font-medium mb-3">Inscreva-se agora</p>
            <h2 className="text-3xl md:text-4xl font-light text-black tracking-tight mb-2">
              Garanta sua vaga
            </h2>
            <p className="text-[#b8962e] text-xs tracking-[0.2em] uppercase font-medium mb-6">
              {course.subtitle} — {course.name}
            </p>
            <p className="text-gray-500 text-sm leading-relaxed mb-10">
              Vagas limitadas por turma. Preencha o formulário ao lado e escolha a melhor forma de pagamento para você.
            </p>

            <div className="space-y-4 mb-10">
              {/* À Vista */}
              <button
                type="button"
                onClick={() => setSelectedPlan("full")}
                className={`w-full text-left border p-6 transition-all cursor-pointer ${
                  selectedPlan === "full" ? "border-[#b8962e] bg-white" : "border-gray-200 bg-white hover:border-gray-400"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs tracking-widest uppercase font-semibold text-black">À Vista</span>
                  <span className="text-xs bg-[#b8962e] text-white px-2 py-0.5 font-medium tracking-wide">Melhor oferta</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-semibold text-black">{course.priceAVista}</span>
                  <span className="text-gray-400 text-sm mb-1">pagamento único</span>
                </div>
                <p className="text-gray-500 text-xs mt-2">Acesso imediato + todos os bônus incluídos</p>
                {selectedPlan === "full" && (
                  <div className="mt-3 flex items-center gap-1 text-[#b8962e]">
                    <i className="ri-checkbox-circle-fill text-sm"></i>
                    <span className="text-xs font-medium">Selecionado</span>
                  </div>
                )}
              </button>

              {/* Parcelado */}
              <button
                type="button"
                onClick={() => setSelectedPlan("installment")}
                className={`w-full text-left border p-6 transition-all cursor-pointer ${
                  selectedPlan === "installment" ? "border-[#b8962e] bg-white" : "border-gray-200 bg-white hover:border-gray-400"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs tracking-widest uppercase font-semibold text-black">Parcelado</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-semibold text-black">{course.nParcelas}x {course.priceParcela}</span>
                  <span className="text-gray-400 text-sm mb-1">sem juros</span>
                </div>
                <p className="text-gray-500 text-xs mt-2">Total de {course.totalParcelado} — Acesso imediato após confirmação</p>
                {selectedPlan === "installment" && (
                  <div className="mt-3 flex items-center gap-1 text-[#b8962e]">
                    <i className="ri-checkbox-circle-fill text-sm"></i>
                    <span className="text-xs font-medium">Selecionado</span>
                  </div>
                )}
              </button>
            </div>

            {/* Garantia */}
            <div className="flex items-start gap-4 border border-gray-100 bg-white p-5">
              <span className="w-10 h-10 flex items-center justify-center shrink-0">
                <i className="ri-shield-check-line text-[#b8962e] text-2xl"></i>
              </span>
              <div>
                <p className="text-black text-sm font-semibold mb-1">Garantia de 7 dias</p>
                <p className="text-gray-500 text-xs leading-relaxed">
                  Não gostou? Devolvemos 100% do seu dinheiro sem perguntas nos primeiros 7 dias.
                </p>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="bg-white border border-gray-100 p-8 md:p-10">
            {status === "success" ? (
              <div className="text-center py-10">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-6 border border-[#b8962e]/40 bg-[#b8962e]/5">
                  <i className="ri-checkbox-circle-line text-[#b8962e] text-3xl"></i>
                </span>
                <p className="text-xs tracking-[0.3em] uppercase text-[#b8962e] font-medium mb-3">
                  Inscrição confirmada
                </p>
                <h3 className="text-black text-2xl font-light tracking-tight mb-4">
                  Bem-vinda ao Método C.A.T!
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-6 max-w-xs mx-auto">
                  Estamos felizes em ter você aqui! Em breve nossa equipe entrará em contato pelo WhatsApp e e-mail com todas as instruções de acesso.
                </p>
                <div className="border-t border-gray-100 pt-6 space-y-3">
                  <div className="flex items-center gap-3 text-left">
                    <span className="w-8 h-8 flex items-center justify-center shrink-0 border border-[#b8962e]/30">
                      <i className="ri-mail-check-line text-[#b8962e] text-sm"></i>
                    </span>
                    <p className="text-xs text-gray-500 leading-snug">Verifique seu e-mail — você receberá sua confirmação em instantes.</p>
                  </div>
                  <div className="flex items-center gap-3 text-left">
                    <span className="w-8 h-8 flex items-center justify-center shrink-0 border border-[#b8962e]/30">
                      <i className="ri-whatsapp-line text-[#b8962e] text-sm"></i>
                    </span>
                    <p className="text-xs text-gray-500 leading-snug">Nossa equipe também vai entrar em contato pelo WhatsApp para orientar os próximos passos.</p>
                  </div>
                  <div className="flex items-center gap-3 text-left">
                    <span className="w-8 h-8 flex items-center justify-center shrink-0 border border-[#b8962e]/30">
                      <i className="ri-trophy-line text-[#b8962e] text-sm"></i>
                    </span>
                    <p className="text-xs text-gray-500 leading-snug">Prepare-se para transformar sua forma de vender. Você fez a escolha certa!</p>
                  </div>
                </div>
                <a
                  href="https://wa.me/5511981969885"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="whitespace-nowrap inline-flex items-center gap-2 mt-8 bg-black text-white text-xs tracking-widest uppercase px-7 py-3 hover:bg-[#b8962e] transition-all duration-300 cursor-pointer font-medium"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-whatsapp-line"></i>
                  </span>
                  Falar com a equipe
                </a>
              </div>
            ) : (
              <form data-readdy-form id={`enrollment-form-${course.name.toLowerCase()}`} onSubmit={handleSubmit} className="space-y-5">
                {/* Course selector */}
                <div>
                  <label className="block text-xs tracking-widest uppercase text-gray-500 mb-3">
                    Escolha seu curso <span className="text-[#b8962e]">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {COURSES.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setSelectedCourseKey(c.key)}
                        className={`flex flex-col items-center gap-0.5 px-2 py-3 border transition-all cursor-pointer ${
                          selectedCourseKey === c.key
                            ? "border-[#b8962e] bg-[#b8962e]/5"
                            : "border-gray-200 bg-white hover:border-gray-400"
                        }`}
                      >
                        <div className={`w-7 h-7 flex items-center justify-center rounded-full mb-1 ${
                          selectedCourseKey === c.key ? "bg-[#b8962e]/15" : "bg-gray-100"
                        }`}>
                          <i className={`${c.icon} ${selectedCourseKey === c.key ? "text-[#b8962e]" : "text-gray-400"} text-xs`}></i>
                        </div>
                        <span className={`text-[11px] font-semibold tracking-wide uppercase ${
                          selectedCourseKey === c.key ? "text-[#b8962e]" : "text-gray-600"
                        }`}>
                          {c.name}
                        </span>
                        <div className="flex flex-col items-center mt-1">
                          <span className="text-[10px] font-medium text-black">{c.priceAVista}</span>
                          <span className="text-[10px] text-gray-400">ou {c.nParcelas}x {c.priceParcela}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
                    Nome completo <span className="text-[#b8962e]">*</span>
                  </label>
                  <input type="text" name="nome" required placeholder="Seu nome completo"
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-400 outline-none focus:border-black transition" />
                </div>
                <div>
                  <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
                    E-mail <span className="text-[#b8962e]">*</span>
                  </label>
                  <input type="email" name="email" required placeholder="seu@email.com"
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-400 outline-none focus:border-black transition" />
                </div>
                <div>
                  <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
                    WhatsApp <span className="text-[#b8962e]">*</span>
                  </label>
                  <input type="tel" name="whatsapp" required placeholder="(00) 00000-0000"
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-400 outline-none focus:border-black transition" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
                      CPF <span className="text-[#b8962e]">*</span>
                    </label>
                    <input type="text" name="cpf" required placeholder="000.000.000-00"
                      maxLength={14}
                      className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-400 outline-none focus:border-black transition" />
                  </div>
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
                      CNPJ
                    </label>
                    <input type="text" name="cnpj" placeholder="00.000.000/0000-00"
                      maxLength={18}
                      className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-400 outline-none focus:border-black transition" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
                    Nome da Loja
                  </label>
                  <input type="text" name="nome_loja" placeholder="Nome da sua loja"
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-400 outline-none focus:border-black transition" />
                </div>
                <div>
                  <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
                    Endereço
                  </label>
                  <input type="text" name="endereco" placeholder="Rua, número, bairro"
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-400 outline-none focus:border-black transition" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
                      Cidade
                    </label>
                    <input type="text" name="cidade" placeholder="Sua cidade"
                      className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-400 outline-none focus:border-black transition" />
                  </div>
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
                      Estado
                    </label>
                    <select name="estado"
                      className="w-full border border-gray-200 px-4 py-3 text-sm text-black outline-none focus:border-black transition bg-white cursor-pointer">
                      <option value="">Selecione</option>
                      <option value="AC">AC</option>
                      <option value="AL">AL</option>
                      <option value="AP">AP</option>
                      <option value="AM">AM</option>
                      <option value="BA">BA</option>
                      <option value="CE">CE</option>
                      <option value="DF">DF</option>
                      <option value="ES">ES</option>
                      <option value="GO">GO</option>
                      <option value="MA">MA</option>
                      <option value="MT">MT</option>
                      <option value="MS">MS</option>
                      <option value="MG">MG</option>
                      <option value="PA">PA</option>
                      <option value="PB">PB</option>
                      <option value="PR">PR</option>
                      <option value="PE">PE</option>
                      <option value="PI">PI</option>
                      <option value="RJ">RJ</option>
                      <option value="RN">RN</option>
                      <option value="RS">RS</option>
                      <option value="RO">RO</option>
                      <option value="RR">RR</option>
                      <option value="SC">SC</option>
                      <option value="SP">SP</option>
                      <option value="SE">SE</option>
                      <option value="TO">TO</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
                    Instagram <span className="text-[#b8962e]">*</span>
                  </label>
                  <input type="text" name="instagram" required placeholder="@suaconta"
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-400 outline-none focus:border-black transition" />
                </div>
                <div>
                  <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
                    Você trabalha com vendas? <span className="text-[#b8962e]">*</span>
                  </label>
                  <select name="area_atuacao" required
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black outline-none focus:border-black transition bg-white cursor-pointer">
                    <option value="">Selecione uma opção</option>
                    <option value="Varejo físico">Varejo físico</option>
                    <option value="E-commerce">E-commerce</option>
                    <option value="Vendas por WhatsApp">Vendas por WhatsApp</option>
                    <option value="Ainda não, quero começar">Ainda não, quero começar</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">
                    Mensagem (opcional)
                  </label>
                  <textarea name="mensagem" rows={3} maxLength={500}
                    placeholder="Conte um pouco sobre sua situação atual..."
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-400 outline-none focus:border-black transition resize-none" />
                </div>
                {status === "error" && (
                  <p className="text-red-500 text-xs tracking-wide">Ocorreu um erro. Tente novamente em instantes.</p>
                )}
                <button type="submit" disabled={status === "loading"}
                  className="whitespace-nowrap w-full bg-black text-white text-xs tracking-widest uppercase py-4 hover:bg-[#b8962e] transition-all duration-300 cursor-pointer font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                  {status === "loading" ? "Enviando..." : "Garantir minha vaga"}
                </button>
                <p className="text-gray-400 text-[11px] text-center leading-relaxed">
                  Ao se inscrever, você concorda com nossa Política de Privacidade.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}